import ApiBrand from '@/models/api/brand'
import { FkuPrice } from '@/models/api/price/fku-price'
import { ApiTag } from '@/models/api/tags'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { createLensDetail, LensDetail } from '@/models/item/fku/contact-lens/lens-detail'
import * as SKUFactory from '@/models/item/sku/factory/sku'
import { ApiImage } from '../../api/image'
import { ApiProductclass } from '../../api/productclass'
import ContactLensSKU from '../sku/contact-lens'

export default class ContactLensFKU {
  static create(fku: ApiProductclass): ContactLensFKU {
    return new ContactLensFKU(fku)
  }

  private constructor(private _fku: ApiProductclass) {}

  /**
   * ConatactLensFKU が ApiProductclass と透過的に利用できる必要があるなら、委譲ではなく継承にしても良いかもしれない.
   * @deprecated rawを露出させず、 ConatactLensFKU が ApiProductclass と互換になったら削除 or private化
   * @see client/src/components/molecules/ItemContactLens.vue:7
   */
  get raw(): ApiProductclass {
    return this._fku
  }

  get id(): string {
    return this._fku.id
  }

  get shortId(): string {
    return this._fku.shortId
  }

  get belongingBrand(): ApiBrand {
    return this._fku.belongingBrand
  }

  get price(): FkuPrice {
    return this._fku.price
  }

  get isOnDiscount(): boolean {
    return this._fku.isOnDiscount
  }

  get productName(): string {
    return this._fku.productName
  }

  get color() {
    return this._fku.color
  }

  get colorLabel(): string {
    return this.color ? this.color.label : ''
  }

  get colorName(): string {
    return this.color ? this.color.name : ''
  }

  get disposalSchedule(): DisposalSchedule {
    if (!this.disposalScheduleTag) {
      return { label: '', styleClass: '' }
    }

    switch (this.disposalScheduleTag.name) {
      case '1day':
        return { label: 'ワンデー', styleClass: 'one-day' }
      case '2weeks':
        return { label: '2週間', styleClass: 'weeks' }
      case '1month':
        return { label: '1ヶ月', styleClass: 'months' }
      case '1year':
        return { label: '1年', styleClass: 'years' }
      default:
        return { label: '', styleClass: '' }
    }
  }

  get lensCountPerBox(): number {
    return this.root ? this.root.get('aux_long_1', 0) : 0
  }

  get bc(): string {
    return this.bcTag ? this.bcTag.label : ''
  }

  get colorDia(): string {
    return this.colorDiaTag ? this.colorDiaTag.label : ''
  }

  get dia(): string {
    return this.diaTag ? this.diaTag.label : ''
  }

  get power(): string | null {
    return this.lensDetail.power
  }

  /**
   * レンズ画像のurlを取得する
   */
  get lensImageUrl(): string {
    return this.lensImage ? this.lensImage.url : ''
  }

  /**
   * レンズ画像(xs)のurlを取得する
   * xsがなければオリジナルにフォールバックする
   */
  get lensImageUrlXs(): string {
    return this.lensImage ? this.lensImage.urlXs || this.lensImageUrl : ''
  }

  get lensImageSize(): { height: number, width: number } | undefined {
    if (!this.lensImage) {
      return undefined
    }

    const height = this.lensImage.height
    const width = this.lensImage.width

    if (!height || !width) {
      return undefined
    }

    return { height, width }
  }

  /**
   * レンズ画像のメディアIDを取得する
   */
  get lensImageMediaId(): string {
    return this.lensImage ? this.lensImage.id : ''
  }

  get supportCompactDelivery(): boolean {
    return this.root ? this.root.supportCompactDelivery : false
  }

  get supportNekoposuDelivery(): boolean {
    return this.root ? this.root.supportNekoposuDelivery : false
  }

  skus(behavior: BehaviorConfig): ContactLensSKU[] {
    return this._fku.products.map(product => SKUFactory.createContactLens(product, behavior))
  }

  get images(): ApiImage[] {
    return this._fku.images
  }

  /**
   * レンズ画像を取得
   */
  private get lensImage(): ApiImage | undefined {
    return this._fku.images.find(image => image.imageAppKind === '1020')
  }

  private get root(): ApiProductclass | null {
    return this._fku.root
  }

  private get disposalScheduleTag(): ApiTag | undefined {
    const rootTags = this.root ? this.root.tags : []

    return rootTags.find(tag => /mycolor\.product\.contactlens\.wear_period\..*/.test(tag.pathname))
  }

  private get bcTag(): ApiTag | undefined {
    const tags = this._fku.tags

    return tags.find(tag => /mycolor\.product\.contactlens\.bc\..*/.test(tag.pathname))
  }

  private get colorDiaTag(): ApiTag | undefined {
    const tags = this._fku.tags

    return tags.find(tag => /mycolor\.product\.contactlens\.color_dia\..*/.test(tag.pathname))
  }

  private get diaTag(): ApiTag | undefined {
    const rootTags = this.root ? this.root.tags : []

    return rootTags.find(tag => /mycolor\.product\.contactlens\.dia\..*/.test(tag.pathname))
  }

  private get lensDetail(): LensDetail {
    return createLensDetail(this.raw)
  }
}

export interface DisposalSchedule {
  label: string
  /**
   * CSS側で、コンタクトの装用期間に応じたスタイル（バッジの色など）を出し分けるための、クラス定義。
   * スタイルへの関心を直接表明せずに、IDやタイプのようなものを返すという考え方もありうるが、ビュー層に閉じている知識でもあり、スタイル用のdiscriminatorとして返している。
   * @see https://github.com/my-color/front/pull/6290#discussion_r838252758
   */
  styleClass: string
}
